import { useEffect, useState } from "react";
import { api } from "./api";
import { Upload, Zap, FileText, CheckCircle2, AlertCircle } from "lucide-react";

const AdminImport = () => {
  const [sources, setSources] = useState([]);
  const [csvSource, setCsvSource] = useState("importyeti");
  const [csvFile, setCsvFile] = useState(null);
  const [csvBusy, setCsvBusy] = useState(false);
  const [csvResult, setCsvResult] = useState(null);
  const [csvErr, setCsvErr] = useState("");

  const [apolloReady, setApolloReady] = useState(null);
  const [apolloBusy, setApolloBusy] = useState(false);
  const [apolloResult, setApolloResult] = useState(null);
  const [apolloErr, setApolloErr] = useState("");
  const [apollo, setApollo] = useState({
    job_titles: "VP Logistics, Head of Fulfillment, E-commerce Operations, Supply Chain Director",
    industries: "logistics, ecommerce, apparel, consumer electronics",
    employee_min: 50, employee_max: 5000, limit: 25,
  });

  useEffect(() => {
    api.listSources().then((r) => setSources(r.data.sources || []));
    api.apolloStatus().then((r) => setApolloReady(r.data.configured)).catch(() => setApolloReady(false));
  }, []);

  const submitCsv = async (e) => {
    e.preventDefault();
    if (!csvFile) return;
    setCsvBusy(true); setCsvErr(""); setCsvResult(null);
    try {
      const r = await api.importCsv(csvSource, csvFile);
      setCsvResult(r.data);
    } catch (e) {
      setCsvErr(e?.response?.data?.detail || e.message);
    } finally { setCsvBusy(false); }
  };

  const submitApollo = async (e) => {
    e.preventDefault();
    setApolloBusy(true); setApolloErr(""); setApolloResult(null);
    try {
      const r = await api.importApollo({
        job_titles: apollo.job_titles.split(",").map((s) => s.trim()).filter(Boolean),
        industries: apollo.industries.split(",").map((s) => s.trim()).filter(Boolean),
        employee_min: Number(apollo.employee_min) || undefined,
        employee_max: Number(apollo.employee_max) || undefined,
        limit: Number(apollo.limit) || 25,
      });
      setApolloResult(r.data);
    } catch (e) {
      const d = e?.response?.data?.detail;
      setApolloErr(typeof d === "string" ? d : (d?.message || e.message));
    } finally { setApolloBusy(false); }
  };

  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-display text-3xl md:text-4xl font-bold">Import Leads</h1>
        <p className="text-white/60 mt-1 text-sm">
          Bulk-add prospects from external channels — ImportYeti / Panjiva CSV exports, or a live Apollo.io search.
        </p>
      </div>

      {/* Apollo */}
      <section className="rounded-2xl bg-[#0f1512] border border-white/10 p-6">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-[var(--tt-zam-orange)]/15 text-[var(--tt-zam-orange)] grid place-items-center">
              <Zap className="h-5 w-5" />
            </div>
            <div>
              <h2 className="font-display text-xl font-bold">Apollo.io — live search</h2>
              <p className="text-xs text-white/50 mt-0.5">People search + bulk enrichment · dedupes by email</p>
            </div>
          </div>
          <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${
            apolloReady === true ? "bg-green-500/10 text-green-400" :
            apolloReady === false ? "bg-red-500/10 text-red-400" :
            "bg-white/5 text-white/50"
          }`}>
            <span className={`h-1.5 w-1.5 rounded-full ${apolloReady ? "bg-green-400" : "bg-red-400"}`} />
            {apolloReady === null ? "Checking…" : apolloReady ? "API key connected" : "API key missing"}
          </span>
        </div>

        <form onSubmit={submitApollo} className="grid sm:grid-cols-2 gap-3">
          <label className="block sm:col-span-2">
            <span className="text-[11px] uppercase tracking-wider text-white/50">Job titles (comma-separated)</span>
            <input value={apollo.job_titles} onChange={(e) => setApollo({ ...apollo, job_titles: e.target.value })} className="mt-1 w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2.5 text-sm" />
          </label>
          <label className="block sm:col-span-2">
            <span className="text-[11px] uppercase tracking-wider text-white/50">Industries / keywords (comma-separated)</span>
            <input value={apollo.industries} onChange={(e) => setApollo({ ...apollo, industries: e.target.value })} className="mt-1 w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2.5 text-sm" />
          </label>
          <label className="block">
            <span className="text-[11px] uppercase tracking-wider text-white/50">Employees — min</span>
            <input type="number" value={apollo.employee_min} onChange={(e) => setApollo({ ...apollo, employee_min: e.target.value })} className="mt-1 w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2.5 text-sm" />
          </label>
          <label className="block">
            <span className="text-[11px] uppercase tracking-wider text-white/50">Employees — max</span>
            <input type="number" value={apollo.employee_max} onChange={(e) => setApollo({ ...apollo, employee_max: e.target.value })} className="mt-1 w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2.5 text-sm" />
          </label>
          <label className="block sm:col-span-2">
            <span className="text-[11px] uppercase tracking-wider text-white/50">Leads to import (max)</span>
            <input type="number" value={apollo.limit} min="1" max="200" onChange={(e) => setApollo({ ...apollo, limit: e.target.value })} className="mt-1 w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2.5 text-sm" />
          </label>
          <div className="sm:col-span-2 flex items-center justify-between gap-3 pt-2">
            <p className="text-[11px] text-white/50">
              {apolloReady === false && "Ask an admin to add APOLLO_API_KEY to backend/.env and restart."}
              {apolloReady && "Uses Apollo credits per enrichment. Runs up to 30s."}
            </p>
            <button data-testid="apollo-import-submit" disabled={apolloBusy || !apolloReady} className="inline-flex items-center gap-1.5 rounded-lg bg-[var(--tt-zam-orange)] hover:bg-[#d16600] px-4 py-2 text-sm font-semibold transition disabled:opacity-50">
              <Zap className="h-4 w-4" /> {apolloBusy ? "Importing…" : "Run Apollo import"}
            </button>
          </div>
        </form>

        {apolloResult && (
          <div className="mt-4 rounded-lg bg-green-500/10 border border-green-500/20 text-green-300 text-sm p-3 flex items-start gap-2">
            <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />
            <span>Imported <b>{apolloResult.imported}</b> leads (searched {apolloResult.searched}). See them on the Leads page.</span>
          </div>
        )}
        {apolloErr && (
          <div className="mt-4 rounded-lg bg-red-500/10 border border-red-500/20 text-red-300 text-sm p-3 flex items-start gap-2">
            <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" /> {apolloErr}
          </div>
        )}
      </section>

      {/* CSV */}
      <section className="rounded-2xl bg-[#0f1512] border border-white/10 p-6">
        <div className="flex items-center gap-3 mb-5">
          <div className="h-10 w-10 rounded-xl bg-[var(--tt-green)]/15 text-[var(--tt-mint)] grid place-items-center">
            <FileText className="h-5 w-5" />
          </div>
          <div>
            <h2 className="font-display text-xl font-bold">CSV import</h2>
            <p className="text-xs text-white/50 mt-0.5">ImportYeti · Panjiva · ImportGenius exports (or any CSV)</p>
          </div>
        </div>

        <form onSubmit={submitCsv} className="grid sm:grid-cols-2 gap-3">
          <label className="block">
            <span className="text-[11px] uppercase tracking-wider text-white/50">Tag as source</span>
            <select value={csvSource} onChange={(e) => setCsvSource(e.target.value)} className="mt-1 w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2.5 text-sm">
              {sources.map((s) => <option key={s.slug} value={s.slug}>{s.name}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-[11px] uppercase tracking-wider text-white/50">CSV file</span>
            <input type="file" accept=".csv,text/csv" onChange={(e) => setCsvFile(e.target.files?.[0] || null)} className="mt-1 w-full text-sm text-white/80 file:mr-3 file:rounded-lg file:border-0 file:bg-white/10 file:text-white file:px-3 file:py-2 file:text-xs file:font-semibold hover:file:bg-white/20 transition" />
          </label>
          <div className="sm:col-span-2 rounded-lg bg-black/30 border border-white/5 p-3 text-[11px] text-white/60">
            <div className="font-semibold mb-1 text-white/80">Expected columns (any subset works):</div>
            <code className="text-[10px]">company, contact_name, title, email, phone, linkedin_url, monthly_volume, marketplaces, notes, tags</code>
          </div>
          <div className="sm:col-span-2 flex justify-end pt-2">
            <button data-testid="csv-import-submit" disabled={csvBusy || !csvFile} className="inline-flex items-center gap-1.5 rounded-lg bg-[var(--tt-green)] hover:bg-[var(--tt-green-dark)] px-4 py-2 text-sm font-semibold transition disabled:opacity-50">
              <Upload className="h-4 w-4" /> {csvBusy ? "Uploading…" : "Import CSV"}
            </button>
          </div>
        </form>

        {csvResult && (
          <div className="mt-4 rounded-lg bg-green-500/10 border border-green-500/20 text-green-300 text-sm p-3 flex items-start gap-2">
            <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />
            <span>Imported <b>{csvResult.imported}</b> leads. Skipped <b>{csvResult.skipped}</b> duplicates / invalid rows.</span>
          </div>
        )}
        {csvErr && (
          <div className="mt-4 rounded-lg bg-red-500/10 border border-red-500/20 text-red-300 text-sm p-3 flex items-start gap-2">
            <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" /> {csvErr}
          </div>
        )}
      </section>
    </div>
  );
};

export default AdminImport;
